import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import {
  browserLocalPersistence,
  browserPopupRedirectResolver,
  connectAuthEmulator,
  getAuth,
  initializeAuth,
} from 'firebase/auth';
import {
  connectFirestoreEmulator,
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  persistentSingleTabManager,
} from 'firebase/firestore';

/** Strip whitespace — vercel env pull sometimes appends literal \\n to values. */
function env(key: string): string | undefined {
  const raw = import.meta.env[key];
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  return trimmed || undefined;
}

const firebaseConfig = {
  apiKey: env('VITE_FIREBASE_API_KEY'),
  authDomain: env('VITE_FIREBASE_AUTH_DOMAIN'),
  projectId: env('VITE_FIREBASE_PROJECT_ID'),
  storageBucket: env('VITE_FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: env('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  appId: env('VITE_FIREBASE_APP_ID'),
};

export const isFirebaseConfigured = Boolean(firebaseConfig.apiKey && firebaseConfig.projectId);

function parseHostPort(value: string, fallbackPort: number): { host: string; port: number } {
  const clean = value.replace(/^https?:\/\//, '');
  const [host, rawPort] = clean.split(':');
  const port = Number(rawPort ?? fallbackPort);
  if (!host || !Number.isFinite(port)) {
    throw new Error(`Invalid Firebase emulator host "${value}". Use host:port, for example localhost:${fallbackPort}.`);
  }
  return { host, port };
}

/**
 * WebKit (iOS Safari / installed iOS PWAs / desktop Safari) has a long-standing
 * IndexedDB lock bug: with multiple tabs or a Safari tab + standalone PWA on the
 * same origin, Firebase Auth's default IndexedDB persistence can leave
 * `onAuthStateChanged` never firing — profile/teams spin forever. Chrome desktop
 * does not hit this.
 *
 * @see https://github.com/firebase/firebase-js-sdk/issues/8019
 */
function isWebKitIndexedDbRisk(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isSafari = /Safari/i.test(ua) && !/Chrome|Chromium|Edg|Android/i.test(ua);
  return isIOS || isSafari;
}

// Initialize once; getApps() guard prevents re-initialization on HMR.
const app = isFirebaseConfigured
  ? (getApps().length ? getApps()[0] : initializeApp(firebaseConfig))
  : null;

function createAuth(firebaseApp: FirebaseApp) {
  if (isWebKitIndexedDbRisk()) {
    try {
      // localStorage persistence avoids the Safari IndexedDB auth lock that
      // freezes iOS PWAs on the auth bootstrap spinner.
      return initializeAuth(firebaseApp, {
        persistence: browserLocalPersistence,
        popupRedirectResolver: browserPopupRedirectResolver,
      });
    } catch {
      // HMR / second import — Auth already initialized on this app.
      return getAuth(firebaseApp);
    }
  }
  return getAuth(firebaseApp);
}

/**
 * Persistent (IndexedDB) local cache: pages that subscribe on mount render
 * instantly from disk and reconcile against the server in the background —
 * the native "show stale, refresh silently" pattern.
 *
 * On WebKit, use the single-tab manager so a Safari tab + installed PWA do not
 * contend for the multi-tab lock (same hang class as Auth above). Elsewhere,
 * multi-tab sharing keeps several open tabs in sync.
 */
function createFirestore(firebaseApp: FirebaseApp) {
  try {
    const tabManager = isWebKitIndexedDbRisk()
      ? persistentSingleTabManager(undefined)
      : persistentMultipleTabManager();
    return initializeFirestore(firebaseApp, {
      localCache: persistentLocalCache({ tabManager }),
    });
  } catch {
    // HMR re-runs this module while the Firebase app (and its Firestore
    // instance) survives, and initializeFirestore throws on a second call.
    return getFirestore(firebaseApp);
  }
}

// Direct exports — no Proxy. Firebase methods rely on `this` being the real
// Auth/Firestore instance, and the Proxy pattern breaks that in subtle ways.
export const auth = app ? createAuth(app) : null!;
export const db = app ? createFirestore(app) : null!;

const emulatorState = globalThis as typeof globalThis & {
  __riftboundFirebaseEmulatorsConnected?: boolean;
};

const useFirebaseEmulators = env('VITE_USE_FIREBASE_EMULATORS') === 'true';

if (app && import.meta.env.DEV && useFirebaseEmulators && !emulatorState.__riftboundFirebaseEmulatorsConnected) {
  const firestoreEmulatorHost = env('VITE_FIRESTORE_EMULATOR_HOST') ?? 'localhost:8080';
  const authEmulatorHost = env('VITE_FIREBASE_AUTH_EMULATOR_HOST') ?? 'localhost:9099';

  if (firestoreEmulatorHost) {
    const { host, port } = parseHostPort(firestoreEmulatorHost, 8080);
    connectFirestoreEmulator(db, host, port);
  }

  if (authEmulatorHost) {
    const cleanHost = authEmulatorHost.replace(/^https?:\/\//, '');
    connectAuthEmulator(auth, `http://${cleanHost}`, { disableWarnings: true });
  }

  emulatorState.__riftboundFirebaseEmulatorsConnected = true;
}
